import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { ExerciseDetail } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/components/exerciseDetail"
import { MediaDetails } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetails"
import { NoteForAthletesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForAthletesDetails"
import { NoteForCoachesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForCoachesDetails"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { EmptyView } from "@/modules/ui/components/emptyView"
import { HURichTextView } from "@/modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@/modules/ui/components/richText/richTextUtils"
import Vector from "@images/background/vector.svg"
import SessionEmpty from "@images/empty/session_empty.svg"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"

type TemplateSessionViewProps = {
  session: TemplateSessionEntity
  className?: string
} & React.ComponentProps<"div">

export function TemplateSessionView({ session, className, ...props }: TemplateSessionViewProps) {
  const { getBlockDurationInterval, timedBlocks, timedBlocksTotal } = useBlockDuration(session.blocks)

  return (
    <div className={cn("relative flex w-full grow flex-col gap-4", className)} {...props}>
      <div className="flex max-h-70 min-h-55 w-full overflow-hidden rounded-b-4xl bg-surface-foreground">
        <div className="flex h-full w-5/10 flex-col items-start justify-between gap-2 pt-5 pb-6 pl-6">
          <div className="flex w-full flex-row justify-end gap-2">
            <div className="flex gap-2">
              {session.gymnasticTag && <GymSelectedIcon color="" width={25} height={25} />}
              {session.weightliftingTag && <WeightliftingSelectedIcon color="" width={25} height={25} />}
              {session.cardioTag && <CardioSelectedIcon color="" width={25} height={25} />}
            </div>
          </div>

          <Text fontStyle="HS" className="w-full wrap-break-word text-textDefault-heading">
            {"restDay" in session && session.restDay ? word("program.onOff.restDay") : session.name}
          </Text>

          <div className="flex max-w-full flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <ProfileAvatar
                profile={{ avatar: session.owner.avatar, username: session.owner.username }}
                className="size-9"
              />
              <Text
                fontStyle="LM"
                className="overflow-hidden text-ellipsis text-textDefault-heading"
              >{`${session.owner.firstname} ${session.owner.lastname}`}</Text>
            </div>
          </div>
        </div>
        <div className="relative h-full w-5/10 overflow-hidden rounded-br-2xl bg-primary-500 [clip-path:polygon(32%_0%,100%_0%,100%_100%,0%_100%)]">
          <img src={Vector} alt="" className="z-5 h-full w-full object-cover" />
        </div>
      </div>

      {session.description && (
        <HURichTextView
          value={formatTextToRichText(session.description)}
          paragrapheFontStyle="BM"
          className="mx-5 mt-2"
        />
      )}

      <div className="flex grow flex-col gap-4 px-5">
        {session.blocks.length > 0 ? (
          <div className="flex flex-col gap-5 pt-2">
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LS" className="text-textDefault-body uppercase">
                {`${word("program.planning.create.exercise.block_plural", { count: session.blocks.length })} (${session.blocks.length.toString()})`}
              </Text>

              {timedBlocks.length > 0 && (
                <div>
                  <Text fontStyle="LS" className="border-r border-borderDefault pr-1 text-textDefault-body uppercase">
                    {`${word("sessionForm.blocks.timed")} (${timedBlocks.length.toString()})`}
                  </Text>
                  <Text fontStyle="LS" className="pl-1 text-textDefault-body">
                    {word("sessionForm.block.totalTime")}
                    <Text fontStyle="LS" className="pl-1 font-bold">
                      {timedBlocksTotal}
                    </Text>
                    {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                  </Text>
                </div>
              )}
            </div>

            {session.blocks.map((block, blockIndex) => (
              <div key={blockIndex}>
                {(block.type === SessionBlockType.SESSION_EXERCISE || block.type === SessionBlockType.COACH_NOTE) &&
                  !!block.content.durationInMin && (
                    <Text fontStyle="BS" className="mb-1 flex flex-row items-center gap-2">
                      <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
                      {getBlockDurationInterval(blockIndex)}
                    </Text>
                  )}
                {block.type === SessionBlockType.SESSION_EXERCISE ? (
                  <ExerciseDetail exercise={block.content} parentSession={null} isCoach={false} />
                ) : block.type === SessionBlockType.COACH_NOTE ? (
                  <NoteForCoachesDetails note={block.content} />
                ) : block.type === SessionBlockType.MEDIAS ? (
                  <MediaDetails medias={block.content.medias} />
                ) : (
                  <NoteForAthletesDetails note={block.content} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyView
            image={{ src: SessionEmpty }}
            title={word("session.empty.title.noExercise")}
            description={word("session.empty.description.noExercise")}
            flexGrow={false}
            className="bg-surface-foreground p-6"
            paddingType="In_table"
          />
        )}
      </div>
    </div>
  )
}
